import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart3, Upload, FileSpreadsheet, LogOut, Download, 
  TrendingUp, Users, ShoppingCart, DollarSign, RefreshCw,
  Clock, Percent, Lightbulb, Package, ChevronRight, Calendar
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from "recharts";
import html2pdf from "html2pdf.js";

// Format number with Czech locale
const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return "0";
  return new Intl.NumberFormat("cs-CZ", { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals 
  }).format(num);
};

const formatCurrency = (num) => {
  if (num === null || num === undefined) return "0 Kč";
  return new Intl.NumberFormat("cs-CZ", { 
    style: "currency", 
    currency: "CZK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color = "primary", delay = 0 }) => (
  <Card 
    className="bg-white border border-[#E2E8F0] shadow-sm animate-fade-in-up"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-sans text-sm text-[#475569] mb-1">{title}</p>
          <p className={`font-oswald text-3xl font-bold ${color === "accent" ? "text-[#FACC15]" : "text-[#0F172A]"}`}>
            {value}
          </p>
          {subtitle && (
            <p className="font-sans text-xs text-[#94A3B8] mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 flex items-center justify-center ${color === "accent" ? "bg-[#FACC15]" : "bg-[#0F172A]"}`}>
          <Icon className={`w-6 h-6 ${color === "accent" ? "text-[#0F172A]" : "text-white"}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Cohort Heatmap Component
const CohortHeatmap = ({ data }) => {
  if (!data || !data.cohorts || data.cohorts.length === 0) return null;

  const getColor = (value) => {
    if (value === 0) return "#F1F5F9";
    if (value < 10) return "#CBD5E1";
    if (value < 25) return "#94A3B8";
    if (value < 50) return "#64748B";
    if (value < 75) return "#334155";
    return "#0F172A";
  };

  const months = ["M0", "M1", "M2", "M3", "M4", "M5", "M6", "M7", "M8", "M9", "M10", "M11"];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="font-oswald text-left p-2 text-[#0F172A] uppercase text-xs">Kohorta</th>
            <th className="font-oswald text-center p-2 text-[#0F172A] uppercase text-xs">Velikost</th>
            {months.map((m) => (
              <th key={m} className="font-oswald text-center p-2 text-[#0F172A] uppercase text-xs">{m}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.cohorts.slice(-8).map((cohort, idx) => (
            <tr key={cohort.cohort} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
              <td className="font-sans p-2 text-[#0F172A] font-medium">{cohort.cohort}</td>
              <td className="font-oswald text-center p-2 text-[#475569]">{cohort.size}</td>
              {months.map((_, i) => {
                const value = cohort[`month_${i}`] || 0;
                return (
                  <td key={i} className="p-1">
                    <div 
                      className="w-full h-8 flex items-center justify-center text-xs font-sans"
                      style={{ 
                        backgroundColor: getColor(value),
                        color: value > 30 ? "white" : "#0F172A"
                      }}
                    >
                      {value > 0 ? `${value}%` : "-"}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Chart colors
const CHART_COLORS = ["#0F172A", "#FACC15", "#94A3B8", "#64748B"];

export default function DashboardPage() {
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);
  const reportRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ];
    
    if (!file.name.endsWith(".csv") && !file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Podporované formáty: CSV, XLSX, XLS");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setMetrics(response.data);
      toast.success("Data úspěšně zpracována");
    } catch (error) {
      const message = error.response?.data?.detail || "Chyba při zpracování souboru";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    setGenerating(true);
    toast.info("Generuji PDF report...");

    try {
      const element = reportRef.current;
      
      // Clone element for PDF to avoid modifying the original
      const clonedElement = element.cloneNode(true);
      
      // Add PDF-specific styles
      clonedElement.style.width = '210mm';
      clonedElement.style.padding = '15mm';
      clonedElement.style.backgroundColor = '#ffffff';
      
      // Remove no-print elements from clone
      const noPrintElements = clonedElement.querySelectorAll('.no-print');
      noPrintElements.forEach(el => el.remove());
      
      // Add page breaks before major sections
      const sections = clonedElement.querySelectorAll('.print-section');
      sections.forEach((section, index) => {
        section.style.pageBreakInside = 'avoid';
        section.style.marginBottom = '20px';
      });
      
      // Ensure charts are visible
      const charts = clonedElement.querySelectorAll('.recharts-wrapper');
      charts.forEach(chart => {
        chart.style.overflow = 'visible';
      });
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `WeLead_Insight_Report_${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          scrollY: 0,
          windowWidth: 1200
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.print-break-before',
          after: '.print-break-after',
          avoid: '.print-section'
        }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success("PDF report vygenerován");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Chyba při generování PDF");
    } finally {
      setGenerating(false);
    }
  };

  // Prepare chart data
  const customerStructureData = metrics ? [
    { name: "1 objednávka", value: metrics.customer_structure?.one_order?.count || 0 },
    { name: "2 objednávky", value: metrics.customer_structure?.two_orders?.count || 0 },
    { name: "3+ objednávek", value: metrics.customer_structure?.three_plus?.count || 0 }
  ] : [];

  const monthlyTrendsData = metrics?.monthly_trends || [];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] text-white sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FACC15] flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-[#0F172A]" />
            </div>
            <span className="font-oswald text-xl font-bold tracking-tight">WELEAD INSIGHT</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-sans text-sm text-white/70">
              {user?.name || user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={logout}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Odhlásit
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Upload Section */}
        {!metrics && (
          <div className="mb-8 animate-fade-in-up">
            <Card className="bg-white border-2 border-dashed border-[#E2E8F0] hover:border-[#FACC15] transition-colors">
              <CardContent className="p-12">
                <div
                  data-testid="upload-dropzone"
                  className={`text-center ${dragActive ? "opacity-50" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="w-20 h-20 bg-[#F1F5F9] mx-auto mb-6 flex items-center justify-center">
                    {loading ? (
                      <RefreshCw className="w-10 h-10 text-[#0F172A] animate-spin" />
                    ) : (
                      <Upload className="w-10 h-10 text-[#0F172A]" />
                    )}
                  </div>
                  <h2 className="font-oswald text-2xl text-[#0F172A] mb-2 uppercase">
                    {loading ? "Zpracovávám data..." : "Nahrajte data objednávek"}
                  </h2>
                  <p className="font-sans text-[#475569] mb-6">
                    Přetáhněte CSV nebo XLSX soubor, nebo klikněte pro výběr
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    data-testid="file-input"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  <Button
                    data-testid="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-oswald uppercase tracking-wide rounded-sm"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Vybrat soubor
                  </Button>
                  <p className="font-sans text-xs text-[#94A3B8] mt-4">
                    Podporované sloupce: date, email, orderItemName, orderItemAmount, totalPriceWithoutVat
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Report Content */}
        {metrics && (
          <div ref={reportRef} className="pdf-content bg-white">
            {/* PDF Header */}
            <div className="mb-8 pb-6 border-b-4 border-[#FACC15] print-section">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-[#FACC15]" />
                </div>
                <div>
                  <h1 className="font-oswald text-3xl text-[#0F172A] uppercase tracking-tight">WeLead Insight Report</h1>
                  <p className="font-sans text-[#475569]">E-commerce Analytics & Customer Intelligence</p>
                </div>
              </div>
              <p className="font-sans text-sm text-[#94A3B8]">
                Vygenerováno: {new Date().toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>

            {/* Action Bar - hidden in PDF */}
            <div className="flex items-center justify-between mb-8 no-print">
              <div className="hidden">
                {/* Hidden duplicate for PDF already shown above */}
              </div>
              <div className="flex gap-3 ml-auto">
                <Button
                  data-testid="new-upload-btn"
                  variant="outline"
                  onClick={() => setMetrics(null)}
                  className="font-oswald uppercase tracking-wide rounded-sm border-[#E2E8F0]"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Nový report
                </Button>
                <Button
                  data-testid="generate-pdf-btn"
                  onClick={generatePDF}
                  disabled={generating}
                  className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0F172A] font-oswald uppercase tracking-wide rounded-sm"
                >
                  {generating ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Stáhnout PDF
                </Button>
              </div>
            </div>

            {/* Executive Summary */}
            <section className="mb-8 print-section">
              <h2 className="font-oswald text-xl text-[#0F172A] uppercase mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Executive Summary
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Celkový obrat"
                  value={formatCurrency(metrics.executive_summary?.total_revenue)}
                  subtitle="bez DPH"
                  icon={DollarSign}
                  delay={0}
                />
                <KPICard
                  title="Počet objednávek"
                  value={formatNumber(metrics.executive_summary?.total_orders)}
                  icon={ShoppingCart}
                  delay={100}
                />
                <KPICard
                  title="Unikátní zákazníci"
                  value={formatNumber(metrics.executive_summary?.unique_customers)}
                  icon={Users}
                  delay={200}
                />
                <KPICard
                  title="Průměrná hodnota objednávky"
                  value={formatCurrency(metrics.executive_summary?.avg_order_value)}
                  icon={TrendingUp}
                  color="accent"
                  delay={300}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <KPICard
                  title="Repeat Rate"
                  value={`${metrics.executive_summary?.repeat_rate || 0}%`}
                  subtitle={`${metrics.executive_summary?.repeat_customers || 0} zákazníků s ≥2 objednávkami`}
                  icon={RefreshCw}
                  delay={400}
                />
                <KPICard
                  title="Průměrné LTV"
                  value={formatCurrency(metrics.executive_summary?.avg_ltv)}
                  subtitle="celkový obrat / unikátní zákazníci"
                  icon={Percent}
                  delay={500}
                />
                <KPICard
                  title="Medián do 2. nákupu"
                  value={metrics.executive_summary?.median_time_to_second_purchase !== null 
                    ? `${metrics.executive_summary?.median_time_to_second_purchase} dní` 
                    : "N/A"}
                  subtitle="pouze zákazníci s ≥2 nákupy"
                  icon={Clock}
                  delay={600}
                />
              </div>

              {/* Validation Check */}
              {metrics.executive_summary?.validation && (
                <div className="mt-4 p-4 bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm" data-testid="validation-check">
                  <p className="font-oswald text-sm text-[#475569] uppercase mb-2">Validace dat (deterministický výpočet)</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm font-sans">
                    <div>
                      <span className="text-[#94A3B8]">Celkem objednávek:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {formatNumber(metrics.executive_summary.validation.all_orders_count)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#94A3B8]">S emailem:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {formatNumber(metrics.executive_summary.validation.orders_with_email)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#94A3B8]">Bez emailu:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {formatNumber(metrics.executive_summary.validation.orders_without_email)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#94A3B8]">Unikátní emaily:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {formatNumber(metrics.executive_summary.validation.unique_emails)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#94A3B8]">Repeat zákazníci:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {formatNumber(metrics.executive_summary.validation.repeat_customers_count)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#94A3B8]">Suma obratu:</span>
                      <span className="ml-2 font-medium text-[#0F172A]">
                        {formatCurrency(metrics.executive_summary.validation.sum_of_revenue)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Day of Week & Hour Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Day of Week */}
              {metrics.executive_summary?.day_analysis && (
                <section className="print-section">
                  <Card className="bg-white border border-[#E2E8F0] h-full" data-testid="day-analysis-chart">
                    <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <CardTitle className="font-oswald text-lg text-[#0F172A] uppercase flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Objednávky podle dne v týdnu
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={metrics.executive_summary.day_analysis}>
                            <XAxis 
                              dataKey="day" 
                              tick={{ fontFamily: "Source Sans 3", fontSize: 11 }}
                              axisLine={{ stroke: "#E2E8F0" }}
                              tickLine={false}
                            />
                            <YAxis 
                              tick={{ fontFamily: "Source Sans 3", fontSize: 11 }}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip 
                              formatter={(value, name) => [
                                `${value}%`, 
                                name === 'orders_pct' ? 'Objednávky' : 'Tržby'
                              ]}
                              contentStyle={{ fontFamily: "Source Sans 3", border: "1px solid #E2E8F0", borderRadius: "2px" }}
                            />
                            <Legend wrapperStyle={{ fontFamily: "Source Sans 3" }} />
                            <Bar dataKey="orders_pct" name="Objednávky %" fill="#0F172A" />
                            <Bar dataKey="revenue_pct" name="Tržby %" fill="#FACC15" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#E2E8F0]">
                              <th className="text-left p-2 font-oswald text-[#0F172A] uppercase text-xs">Den</th>
                              <th className="text-right p-2 font-oswald text-[#0F172A] uppercase text-xs">Obj.</th>
                              <th className="text-right p-2 font-oswald text-[#0F172A] uppercase text-xs">%</th>
                              <th className="text-right p-2 font-oswald text-[#0F172A] uppercase text-xs">Tržby</th>
                              <th className="text-right p-2 font-oswald text-[#0F172A] uppercase text-xs">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {metrics.executive_summary.day_analysis.map((day, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                                <td className="p-2 font-sans text-[#0F172A]">{day.day}</td>
                                <td className="p-2 text-right font-oswald text-[#0F172A]">{formatNumber(day.orders)}</td>
                                <td className="p-2 text-right font-oswald text-[#FACC15]">{day.orders_pct}%</td>
                                <td className="p-2 text-right font-oswald text-[#0F172A]">{formatCurrency(day.revenue)}</td>
                                <td className="p-2 text-right font-oswald text-[#FACC15]">{day.revenue_pct}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* Hour of Day */}
              {metrics.executive_summary?.hour_analysis && (
                <section className="print-section">
                  <Card className="bg-white border border-[#E2E8F0] h-full" data-testid="hour-analysis-chart">
                    <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <CardTitle className="font-oswald text-lg text-[#0F172A] uppercase flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Objednávky podle hodiny
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={metrics.executive_summary.hour_analysis.filter(h => h.orders > 0)}>
                            <XAxis 
                              dataKey="hour" 
                              tick={{ fontFamily: "Source Sans 3", fontSize: 11 }}
                              axisLine={{ stroke: "#E2E8F0" }}
                              tickLine={false}
                              tickFormatter={(h) => `${h}h`}
                            />
                            <YAxis 
                              tick={{ fontFamily: "Source Sans 3", fontSize: 11 }}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip 
                              formatter={(value, name) => [
                                `${value}%`, 
                                name === 'orders_pct' ? 'Objednávky' : 'Tržby'
                              ]}
                              labelFormatter={(h) => `${h}:00 - ${h}:59`}
                              contentStyle={{ fontFamily: "Source Sans 3", border: "1px solid #E2E8F0", borderRadius: "2px" }}
                            />
                            <Area type="monotone" dataKey="orders_pct" name="Objednávky %" stroke="#0F172A" fill="#0F172A" fillOpacity={0.2} strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 grid grid-cols-6 gap-2 text-xs">
                        {metrics.executive_summary.hour_analysis.filter(h => h.orders > 0).map((hour) => (
                          <div key={hour.hour} className="text-center p-2 bg-[#F8FAFC] border border-[#E2E8F0]">
                            <div className="font-oswald text-[#0F172A]">{hour.hour}h</div>
                            <div className="font-oswald text-[#FACC15]">{hour.orders_pct}%</div>
                            <div className="text-[#94A3B8]">{formatNumber(hour.orders)}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </section>
              )}
            </div>

            {/* Insights Section */}
            {metrics.insights && metrics.insights.length > 0 && (
              <section className="mb-8 print-section">
                <h2 className="font-oswald text-xl text-[#0F172A] uppercase mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-[#FACC15]" />
                  Klíčové poznatky & Doporučení
                </h2>
                <Card className="bg-white border border-[#E2E8F0]" data-testid="insights-card">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {metrics.insights.map((insight, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-start gap-3 p-4 bg-[#F8FAFC] border-l-4 border-[#FACC15]"
                        >
                          <ChevronRight className="w-5 h-5 text-[#FACC15] flex-shrink-0 mt-0.5" />
                          <p className="font-serif text-[#0F172A] leading-relaxed">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Customer Structure */}
              <section className="print-section">
                <Card className="bg-white border border-[#E2E8F0] h-full" data-testid="customer-structure-chart">
                  <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <CardTitle className="font-oswald text-lg text-[#0F172A] uppercase flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Struktura zákazníků
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={customerStructureData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {customerStructureData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => formatNumber(value)}
                            contentStyle={{ 
                              fontFamily: "Source Sans 3", 
                              border: "1px solid #E2E8F0",
                              borderRadius: "2px"
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {metrics.customer_structure?.pareto && (
                      <div className="mt-4 p-4 bg-[#FACC15]/10 border border-[#FACC15]/30">
                        <p className="font-sans text-sm text-[#0F172A]">
                          <strong className="font-oswald">Pareto efekt:</strong> Top {metrics.customer_structure.pareto.top_10_customers} zákazníků 
                          generuje <span className="font-oswald text-[#FACC15]">{metrics.customer_structure.pareto.revenue_percentage}%</span> obratu
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>

              {/* Monthly Revenue */}
              <section className="print-section">
                <Card className="bg-white border border-[#E2E8F0] h-full" data-testid="monthly-revenue-chart">
                  <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <CardTitle className="font-oswald text-lg text-[#0F172A] uppercase flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Měsíční obrat
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyTrendsData}>
                          <XAxis 
                            dataKey="month" 
                            tick={{ fontFamily: "Source Sans 3", fontSize: 12 }}
                            axisLine={{ stroke: "#E2E8F0" }}
                            tickLine={false}
                          />
                          <YAxis 
                            tick={{ fontFamily: "Source Sans 3", fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                          />
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ 
                              fontFamily: "Source Sans 3", 
                              border: "1px solid #E2E8F0",
                              borderRadius: "2px"
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="revenue" 
                            stroke="#0F172A" 
                            fill="#0F172A" 
                            fillOpacity={0.1}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </section>
            </div>

            {/* Monthly Trends Bar Chart */}
            <section className="mb-8 print-section print-break">
              <Card className="bg-white border border-[#E2E8F0]" data-testid="monthly-trends-chart">
                <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <CardTitle className="font-oswald text-lg text-[#0F172A] uppercase flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Měsíční trendy - Noví vs vracející se zákazníci
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyTrendsData}>
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontFamily: "Source Sans 3", fontSize: 12 }}
                          axisLine={{ stroke: "#E2E8F0" }}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontFamily: "Source Sans 3", fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            fontFamily: "Source Sans 3", 
                            border: "1px solid #E2E8F0",
                            borderRadius: "2px"
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ fontFamily: "Source Sans 3" }}
                        />
                        <Bar dataKey="new_customers" name="Noví zákazníci" fill="#0F172A" />
                        <Bar dataKey="returning_customers" name="Vracející se" fill="#FACC15" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Cohort Analysis */}
            <section className="mb-8 print-section">
              <Card className="bg-white border border-[#E2E8F0]" data-testid="cohort-heatmap">
                <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <CardTitle className="font-oswald text-lg text-[#0F172A] uppercase flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Kohortní analýza - Retention
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <CohortHeatmap data={metrics.cohort_analysis} />
                  <p className="font-sans text-xs text-[#94A3B8] mt-4">
                    M0 = měsíc první objednávky, M1-M11 = procento zákazníků z kohorty, kteří nakoupili v daném měsíci
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Product Insights */}
            <section className="mb-8 print-section print-break">
              <h2 className="font-oswald text-xl text-[#0F172A] uppercase mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Produktové insighty
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top by Revenue */}
                <Card className="bg-white border border-[#E2E8F0]" data-testid="top-products-revenue">
                  <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC] p-4">
                    <CardTitle className="font-oswald text-sm text-[#0F172A] uppercase">
                      Top 10 podle obratu
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-[#E2E8F0]">
                      {metrics.product_insights?.top_by_revenue?.slice(0, 10).map((product, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 hover:bg-[#F8FAFC]">
                          <span className="font-sans text-sm text-[#0F172A] truncate flex-1 mr-2">
                            {idx + 1}. {product.product}
                          </span>
                          <span className="font-oswald text-sm text-[#0F172A] font-medium whitespace-nowrap">
                            {formatCurrency(product.revenue)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top by Quantity */}
                <Card className="bg-white border border-[#E2E8F0]" data-testid="top-products-quantity">
                  <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC] p-4">
                    <CardTitle className="font-oswald text-sm text-[#0F172A] uppercase">
                      Top 10 podle množství
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-[#E2E8F0]">
                      {metrics.product_insights?.top_by_quantity?.slice(0, 10).map((product, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 hover:bg-[#F8FAFC]">
                          <span className="font-sans text-sm text-[#0F172A] truncate flex-1 mr-2">
                            {idx + 1}. {product.product}
                          </span>
                          <span className="font-oswald text-sm text-[#FACC15] font-medium">
                            {formatNumber(product.quantity)} ks
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top First Order Products */}
                <Card className="bg-white border border-[#E2E8F0]" data-testid="top-first-order-products">
                  <CardHeader className="border-b border-[#E2E8F0] bg-[#F8FAFC] p-4">
                    <CardTitle className="font-oswald text-sm text-[#0F172A] uppercase">
                      Top produkty v 1. objednávce
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-[#E2E8F0]">
                      {metrics.product_insights?.top_first_order?.slice(0, 10).map((product, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 hover:bg-[#F8FAFC]">
                          <span className="font-sans text-sm text-[#0F172A] truncate flex-1 mr-2">
                            {idx + 1}. {product.product}
                          </span>
                          <span className="font-oswald text-sm text-[#94A3B8] font-medium">
                            {formatNumber(product.count)}×
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Footer for PDF */}
            <div className="text-center py-8 border-t-2 border-[#FACC15] mt-8 print-section">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="w-8 h-8 bg-[#0F172A] flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-[#FACC15]" />
                </div>
                <p className="font-oswald text-xl text-[#0F172A] uppercase">WeLead Insight Report</p>
              </div>
              <p className="font-sans text-sm text-[#94A3B8]">
                Vygenerováno: {new Date().toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" })} | Důvěrné
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
